import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  GraduationCap, BookOpen, Award, TrendingUp, Sparkles, ArrowRight,
  Calendar, CheckCircle2, AlertCircle, Link2, Unlink, UserCheck, Loader2, Trophy, Flame,
} from 'lucide-react';
import { currentPalier, nextPalier, PALIERS, haptic } from '../lib/paliers';
import { celebrate } from '../lib/celebrate';
import ActuStoriesCarousel from '../components/ActuStoriesCarousel';
import { MyBilansSection, MaFormationCard } from '../components/StudentBilansSection';
import MyShortsWidget from '../components/MyShortsWidget';

const COMP_LABELS = {
  C1: 'C1 · Maîtrise du véhicule',
  C2: 'C2 · Circulation',
  C3: 'C3 · Conditions difficiles',
  C4: 'C4 · Autonomie',
};
const COMP_TOTAL = { C1: 12, C2: 8, C3: 11, C4: 7 };
const TYPE_LABEL = { conduite: 'Conduite', code: 'Code', examen: 'Examen', rvp: 'RVP', moto_plateau: '🏍 Moto · Plateau' };

export default function StudentDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [badges, setBadges] = useState({ items: [], perfect_count: 0, total_count: 0 });
  const [streak, setStreak] = useState({ streak: 0, active_days_total: 0 });
  const [planning, setPlanning] = useState(null);
  const [nextPrep, setNextPrep] = useState(null);
  const [code, setCode] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkMsg, setLinkMsg] = useState('');
  const [linkErr, setLinkErr] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    refresh();
  }, [authLoading, user, navigate]);

  const refresh = async () => {
    try {
      const d = await api.studentDashboard();
      setData(d);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || 'Erreur de chargement' });
    }
    // Prochains RDV (planning secrétariat)
    try {
      const p = await api.myPlanning();
      setPlanning(p.items || []);
    } catch (_) { setPlanning([]); }
    // Objectifs du prochain cours (fiche de préparation enseignant)
    try {
      const np = await api.studentNextPrep();
      setNextPrep(np && np.token ? np : null);
    } catch (_) { setNextPrep(null); }
    // Charge aussi les badges QCM (silencieux si échoue)
    try {
      const b = await api.studentQcmBadges();
      setBadges(b || { items: [], perfect_count: 0, total_count: 0 });
    } catch (_) { /* silent */ }
    // Ping l'activité du jour + récupère le streak
    try {
      const s = await api.studentPingActivity();
      setStreak(s || { streak: 0, active_days_total: 0 });
    } catch (_) { /* silent */ }
  };

  // Déblocage de palier : compare le palier actuel avec celui mémorisé en localStorage
  useEffect(() => {
    if (!user || badges.perfect_count === undefined) return;
    const key = `cv_palier_seen_${user.id}`;
    const seenStr = (() => { try { return localStorage.getItem(key) || '0'; } catch (_) { return '0'; } })();
    const seen = parseInt(seenStr, 10) || 0;
    const cur = currentPalier(badges.perfect_count);
    if (cur && cur.threshold > seen) {
      // 🎉 Palier débloqué !
      celebrate();
      setTimeout(() => celebrate(), 350);
      setTimeout(() => celebrate(), 700);
      haptic([80, 60, 80, 60, 200]);
      try { localStorage.setItem(key, String(cur.threshold)); } catch (_) { /* silent */ }
    }
  }, [user, badges.perfect_count]);

  const link = async (e) => {
    e.preventDefault();
    setLinking(true); setLinkErr(''); setLinkMsg('');
    try {
      const r = await api.linkTeacher(code.trim().toUpperCase());
      setLinkMsg(`Lié à ${r.teacher_name}`);
      setCode('');
      await refresh();
    } catch (err) {
      setLinkErr(err?.response?.data?.detail || 'Code invalide');
    } finally { setLinking(false); }
  };

  const unlink = async () => {
    if (!window.confirm('Supprimer le lien avec ton enseignant ?')) return;
    try { await api.unlinkTeacher(); await refresh(); } catch (_) { /* silent */ }
  };

  if (authLoading || !data) {
    return <PageShell><div className="max-w-5xl mx-auto px-4 lg:px-6 mt-16 text-white/60"><Loader2 className="animate-spin" /> Chargement…</div></PageShell>;
  }

  if (data.error) {
    return <PageShell><div className="max-w-5xl mx-auto px-4 lg:px-6 mt-16 text-red-300">{data.error}</div></PageShell>;
  }

  const todayIso = new Date().toISOString().slice(0, 10);
  const totalDone = Object.values(data.by_competence || {}).reduce((a, b) => a + (b.done || 0), 0);
  const totalValidated = Object.values(data.by_competence || {}).reduce((a, b) => a + (b.validated || 0), 0);
  const overallPct = Math.round((totalDone / 38) * 100);

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12">
        <SectionTitle
          kicker={`Bonjour ${data.user.name?.split(' ')[0] || ''}`}
          title="Ma journée"
          sub="Le déroulé clair de ton parcours : tes prochains rendez-vous, ta formation, tes comptes-rendus puis ta progression."
        />

        {/* 📅 1. Mes prochains rendez-vous */}
        <div className="mb-5 sm:mb-6 rounded-2xl border border-red-900/40 bg-gradient-to-br from-red-950/25 via-[#140a0a] to-[#0d0d0e] p-5 sm:p-6" data-testid="dash-next-rdv">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-red-600/20 border border-red-700/40 flex items-center justify-center text-red-400 shrink-0"><Calendar size={16} /></div>
              <div className="min-w-0">
                <div className="text-white/55 text-[10.5px] uppercase tracking-widest font-mono-spec">Mes prochains rendez-vous</div>
                <div className="text-white text-[15px] font-semibold truncate">
                  {planning === null ? 'Chargement…' : planning.length === 0 ? 'Aucune leçon planifiée' : `${planning.length} leçon${planning.length > 1 ? 's' : ''} à venir`}
                </div>
              </div>
            </div>
            <Link to="/mon-planning" data-testid="dash-planning-link" className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 text-[12px] font-semibold">
              Tout voir <ArrowRight size={12} />
            </Link>
          </div>
          {planning && planning.length > 0 ? (
            <div className="space-y-2.5">
              {planning.slice(0, 3).map((l, i) => {
                const isToday = l.date === todayIso;
                return (
                  <div key={l.id || i} className={`flex items-center gap-3 rounded-xl border p-3.5 ${isToday ? 'border-amber-500/50 bg-amber-500/[0.07]' : 'border-white/10 bg-white/[0.03]'}`} data-testid={`dash-rdv-${i}`}>
                    <div className={`shrink-0 w-12 text-center rounded-lg py-1.5 ${isToday ? 'bg-amber-500/20 text-amber-300' : 'bg-white/5 text-white/70'}`}>
                      <div className="text-[16px] font-bold leading-none">{new Date(l.date + 'T12:00:00').getDate()}</div>
                      <div className="text-[9.5px] uppercase font-mono-spec mt-0.5">{new Date(l.date + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'short' })}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-[13.5px] font-semibold truncate">
                        {isToday && <span className="text-amber-300">📍 Aujourd&apos;hui · </span>}
                        {l.start_time} · {TYPE_LABEL[l.type] || l.type}
                      </div>
                      <div className="text-white/55 text-[12px] truncate">Avec {l.teacher_name} · {l.duration_min} min</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : planning && (
            <p className="text-white/50 text-[13px]">Ta prochaine leçon apparaîtra ici dès qu&apos;elle sera planifiée par le secrétariat.</p>
          )}
        </div>

        {/* 📚 À réviser avant le prochain cours (fiche de préparation enseignant) */}
        {nextPrep && (
          <div className="mb-5 sm:mb-6 rounded-2xl border border-sky-500/40 bg-gradient-to-br from-sky-950/40 via-[#0a1018] to-[#0d0d0e] p-5 sm:p-6" data-testid="dash-next-prep">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-9 h-9 rounded-xl bg-sky-600/20 border border-sky-500/40 flex items-center justify-center text-sky-300 shrink-0"><BookOpen size={16} /></div>
              <div className="min-w-0">
                <div className="text-sky-300 text-[10.5px] uppercase tracking-widest font-mono-spec font-bold">À réviser avant ton prochain cours</div>
                <div className="text-white/60 text-[12px] truncate">Demandé par {nextPrep.teacher_name}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(nextPrep.steps || []).map((s) => (
                <span key={s.id} className="px-2.5 py-1 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-100 text-[12px] font-semibold">{s.id}</span>
              ))}
            </div>
            {nextPrep.notes && <p className="text-white/75 text-[13px] leading-relaxed mb-3 italic">« {nextPrep.notes} »</p>}
            <div className="flex flex-wrap gap-2">
              <Link to={`/s/${nextPrep.token}`} data-testid="dash-prep-open" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-sky-950 text-[12.5px] font-bold">Voir le détail <ArrowRight size={12} /></Link>
              <Link to="/cours" data-testid="dash-prep-cours" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-white/15 text-white/80 hover:bg-white/5 text-[12.5px] font-semibold">Réviser les cours</Link>
            </div>
          </div>
        )}

        {/* 🎓 2. Ma formation (formule, heures, prochaine leçon) */}
        <MaFormationCard />

        {/* 📝 3. Mes comptes-rendus / bilans reçus */}
        <MyBilansSection />

        {/* 🎬 4. Mes shorts : statuts en attente / publiés / refusés */}
        <MyShortsWidget basePath="/eleve" />

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-4 sm:gap-6">
          {/* Progression */}
          <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4 sm:mb-5 gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><TrendingUp size={18} /></div>
                <div className="min-w-0">
                  <div className="text-white/60 text-[10.5px] sm:text-[12px] uppercase tracking-wider font-mono-spec">Progression REMC</div>
                  <div className="text-white text-[14px] sm:text-[16px] font-semibold truncate">{totalDone}/38 validés</div>
                </div>
              </div>
              <div className="font-serif-editorial text-red-500 text-[32px] sm:text-[44px] leading-none">{overallPct}<span className="text-[16px] sm:text-[20px] text-white/50">%</span></div>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden mb-5 sm:mb-7">
              <div className="h-full bg-gradient-to-r from-red-600 to-red-400" style={{ width: `${overallPct}%` }} />
            </div>

            <div className="space-y-4">
              {['C1', 'C2', 'C3', 'C4'].map((c) => {
                const e = data.by_competence?.[c] || { done: 0, validated: 0 };
                const total = COMP_TOTAL[c];
                const pct = Math.round((e.done / total) * 100);
                return (
                  <div key={c} data-testid={`dash-comp-${c.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-white text-[13.5px] font-semibold">{COMP_LABELS[c]}</div>
                      <div className="text-white/55 text-[12.5px] font-mono-spec">{e.done}/{total} <span className="text-green-400 ml-2">{e.validated > 0 ? `✓ ${e.validated} validé(s) par moniteur` : ''}</span></div>
                    </div>
                    <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className={`h-full ${pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-orange-400' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <Link to="/livret" data-testid="dash-livret-link" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold">
              <BookOpen size={14} /> Continuer mon livret <ArrowRight size={14} />
            </Link>
          </div>

          {/* Enseignant */}
          <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><GraduationCap size={18} /></div>
              <div>
                <div className="text-white/60 text-[12px] uppercase tracking-wider font-mono-spec">Mon enseignant ECSR</div>
                <div className="text-white text-[16px] font-semibold">{data.teacher ? data.teacher.name : 'Non lié'}</div>
              </div>
            </div>
            {data.teacher ? (
              <>
                <p className="text-white/65 text-[13.5px] mb-4">{data.teacher.email}</p>
                <div className="rounded-xl border border-green-900/40 bg-green-950/15 px-4 py-3 text-green-200/85 text-[12.5px] flex items-center gap-2">
                  <UserCheck size={14} /> Ton moniteur peut consulter et valider tes compétences REMC.
                </div>
                <button data-testid="dash-unlink-btn" onClick={unlink} className="mt-5 inline-flex items-center gap-2 text-white/55 hover:text-red-300 text-[12.5px]">
                  <Unlink size={12} /> Délier mon enseignant
                </button>
              </>
            ) : (
              <form onSubmit={link} className="space-y-3">
                <p className="text-white/65 text-[13px]">Saisis le <strong className="text-white">code à 6 caractères</strong> que t&apos;a donné ton moniteur ECSR pour qu&apos;il puisse suivre ta progression.</p>
                <input
                  data-testid="dash-teacher-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="EX. AX5K2P"
                  maxLength={10}
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/15 text-white font-mono-spec uppercase tracking-widest outline-none focus:border-red-500"
                />
                <button data-testid="dash-link-btn" type="submit" disabled={linking || !code.trim()} className="w-full h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {linking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Lier mon enseignant
                </button>
                {linkErr && <p className="text-red-400 text-[12.5px]">{linkErr}</p>}
                {linkMsg && <p className="text-green-400 text-[12.5px]">{linkMsg}</p>}
              </form>
            )}
          </div>
        </div>

        {/* Suggestion du jour */}
        {data.suggestion && (
          <div className="mt-5 sm:mt-6 rounded-xl sm:rounded-2xl border border-red-900/30 bg-gradient-to-br from-red-950/20 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-7">
            <div className="flex items-center gap-2 mb-3 text-red-400">
              <Sparkles size={14} /><span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold">Suggestion du jour</span>
            </div>
            <h3 className="font-serif-editorial text-white text-[18px] sm:text-[24px] lg:text-[30px] leading-tight">{data.suggestion.reason} — un coup d&apos;œil ?</h3>
            <div className="mt-4 sm:mt-5 flex flex-wrap gap-2 sm:gap-3">
              <Link to={`/magazine/${data.suggestion.article_slug}`} data-testid="dash-suggest-article" className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] sm:text-[13.5px] font-semibold">
                <BookOpen size={14} /> Lire l&apos;article <ArrowRight size={14} />
              </Link>
              <Link to={data.suggestion.tool.url} data-testid="dash-suggest-tool" className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full border border-white/15 text-white hover:bg-white/5 text-[12.5px] sm:text-[13.5px] font-semibold">
                <Award size={14} /> Tester · {data.suggestion.tool.name}
              </Link>
            </div>
          </div>
        )}

        {/* Séances récentes */}
        <div className="mt-5 sm:mt-6 rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
          <div className="flex items-center gap-3 mb-4 sm:mb-5">
            <Calendar className="text-red-400" size={18} />
            <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px]">Mes séances pédagogiques</h3>
          </div>
          {data.recent_seances?.length ? (
            <div className="space-y-3">
              {data.recent_seances.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.03]">
                  <div className="flex items-center gap-3 min-w-0">
                    {s.status === 'done' ? <CheckCircle2 size={16} className="text-green-400 shrink-0" /> : <AlertCircle size={16} className="text-orange-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-white text-[14px] font-semibold truncate">{s.title}</div>
                      <div className="text-white/50 text-[12px]">Avec {s.teacher_name} · {s.duration_min} min · {s.status === 'done' ? 'Terminée' : s.status === 'in_progress' ? 'En cours' : 'Planifiée'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/55 text-[13.5px]">Aucune séance pour l&apos;instant. Ton enseignant pourra t&apos;inscrire à un cours en salle ou un RDV pédagogique.</p>
          )}
        </div>

        {/* ═══ BADGES QCM (Séances flash) — gamification retour app */}
        <div id="dash-qcm-badges" className="mt-6 rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 scroll-mt-24" data-testid="dash-qcm-badges">
          {(() => {
            const cur = currentPalier(badges.perfect_count);
            const nxt = nextPalier(badges.perfect_count);
            const prevThresh = cur ? cur.threshold : 0;
            const nxtThresh = nxt ? nxt.threshold : Math.max(prevThresh, 1);
            const progressPct = nxt
              ? Math.min(100, Math.max(0, ((badges.perfect_count - prevThresh) / (nxtThresh - prevThresh)) * 100))
              : 100;
            return (
              <>
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center text-2xl ${cur ? `${cur.bgTile} ${cur.borderTile}` : 'bg-white/5 border-white/10 grayscale opacity-70'}`}>
                      {cur ? cur.emoji : '🎯'}
                    </div>
                    <div>
                      <div className="text-white/60 text-[10.5px] sm:text-[11.5px] uppercase tracking-wider font-mono-spec">Palier de collection</div>
                      <div className="text-white text-[16px] sm:text-[18px] font-semibold">
                        {cur ? cur.name : 'Débute ta collection'}
                        <span className="ml-2 text-white/40 font-mono-spec text-[13px]">· {badges.perfect_count} 🏆</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider">Tentatives</div>
                    <div className="font-serif-editorial text-white text-[24px] leading-none mt-0.5">{badges.total_count}</div>
                  </div>
                </div>

                {/* Barre de progression vers le prochain palier */}
                {nxt ? (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1.5 text-[11.5px]">
                      <span className="text-white/60">Prochain palier · <b className="text-white">{nxt.emoji} {nxt.name}</b></span>
                      <span className="font-mono-spec text-white/80">{badges.perfect_count}/{nxtThresh}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${nxt.bar} transition-all duration-1000`} style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="mt-1.5 text-white/40 text-[11px]">
                      {nxtThresh - badges.perfect_count} sans-faute{nxtThresh - badges.perfect_count > 1 ? 's' : ''} de plus pour débloquer <b className="text-white/70">{nxt.name}</b>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-rose-200 text-[12.5px] font-semibold text-center">
                    👑 Tu as atteint le palier maximum · Légende de Code Voiture !
                  </div>
                )}

                {/* Aperçu de tous les paliers en mini */}
                <div className="mb-5 flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="dash-paliers-list">
                  {PALIERS.map((p) => {
                    const unlocked = badges.perfect_count >= p.threshold;
                    return (
                      <div
                        key={p.threshold}
                        className={`shrink-0 rounded-lg border px-2.5 py-1.5 text-[11px] font-mono-spec flex items-center gap-1.5 ${unlocked ? `${p.bgTile} ${p.borderTile} text-white` : 'bg-white/[0.02] border-white/10 text-white/40 grayscale'}`}
                        title={`${p.name} · ${p.threshold} sans-faute${p.threshold > 1 ? 's' : ''}`}
                      >
                        <span className={unlocked ? '' : 'opacity-50'}>{p.emoji}</span>
                        <span>{p.name}</span>
                        <span className="opacity-60">×{p.threshold}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="text-white/60 text-[10.5px] sm:text-[11.5px] uppercase tracking-wider font-mono-spec inline-flex items-center gap-1.5"><Trophy size={11} /> Mes séances flash</div>
          </div>

          {badges.items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/15 bg-white/[0.02] p-5 text-center">
              <div className="text-2xl mb-1.5">🎯</div>
              <p className="text-white/60 text-[13px] leading-relaxed">
                Aucun badge encore. La prochaine fois que ton moniteur te donnera un <b className="text-white">QR code de séance</b>, scanne-le et fais le mini-QCM pour décrocher ton premier trophée !
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {badges.items.map((b) => (
                <a
                  key={b.token}
                  href={`/s/${b.token}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`dash-badge-${b.token}`}
                  className={`group relative rounded-xl border p-3 transition-colors ${b.is_perfect ? 'border-amber-500/50 bg-gradient-to-br from-amber-500/15 to-transparent hover:from-amber-500/25' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}
                >
                  {b.is_perfect && (
                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-amber-400 text-black font-bold text-[12px] flex items-center justify-center shadow-lg">🏆</div>
                  )}
                  <div className={`text-[24px] mb-1 ${b.is_perfect ? '' : 'grayscale opacity-70'}`}>{b.is_perfect ? '🏆' : '🎯'}</div>
                  <div className={`font-mono-spec text-[13px] font-bold ${b.is_perfect ? 'text-amber-200' : 'text-white'}`}>{b.score}/{b.total}</div>
                  <div className="text-white/50 text-[10.5px] mt-0.5 leading-tight truncate" title={b.teacher_name}>{b.teacher_name}</div>
                  <div className="text-white/30 text-[10px] mt-1 font-mono-spec">
                    {b.updated_at ? new Date(b.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* 🔥 Fun & motivation — streak + fil du jour (en fin de parcours) */}
        <div className="mt-6 mb-4 sm:mb-6 rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 via-red-500/5 to-transparent p-4 sm:p-5 flex items-center gap-3 sm:gap-4" data-testid="dash-streak">
          <div className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-2xl sm:text-3xl ${streak.streak >= 3 ? 'bg-orange-500/25 border border-orange-500/60 shadow-lg shadow-orange-500/20' : 'bg-white/5 border border-white/10 grayscale opacity-70'}`}>
            🔥
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <div className="font-serif-editorial text-white text-[26px] sm:text-[32px] leading-none">{streak.streak}</div>
              <div className="text-white/60 text-[12px] sm:text-[13px] font-mono-spec uppercase tracking-widest">jour{streak.streak > 1 ? 's' : ''} de suite</div>
            </div>
            <div className="text-white/60 text-[11.5px] sm:text-[12.5px] mt-0.5 leading-tight">
              {streak.streak === 0 && 'Reviens demain pour lancer ta série ! 💪'}
              {streak.streak === 1 && 'Bon début ! Reviens demain pour enchaîner.'}
              {streak.streak >= 2 && streak.streak < 7 && `Continue comme ça, ${7 - streak.streak} jour${7 - streak.streak > 1 ? 's' : ''} pour la première semaine complète 🎯`}
              {streak.streak >= 7 && streak.streak < 30 && `Excellent rythme ! Objectif : le mois complet 🏆`}
              {streak.streak >= 30 && `Tu es une vraie machine ! ${streak.streak} jours consécutifs 👑`}
            </div>
          </div>
          <div className="hidden sm:block text-right shrink-0">
            <div className="font-serif-editorial text-white text-[22px] leading-none">{streak.active_days_total}</div>
            <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider">Jours totaux</div>
          </div>
        </div>

        {/* 🌟 Actu de la vie & du fun — inclut posts de l'enseignant + contenu du jour */}
        <div className="mb-6 sm:mb-8 -mx-4 lg:-mx-6">
          <ActuStoriesCarousel
            mode="student"
            title="Ton fil du jour"
            subtitle="Message de ton enseignant, défi du jour, saviez-vous et citations pour te booster."
          />
        </div>
      </section>
    </PageShell>
  );
}
